import { useState } from "react";

const PROJECT_ID = "73acebca-6391-4da3-b270-472de16380f3";
const EDGE_FUNCTION_URL = "https://debuqhzswiqcosjkuktw.supabase.co/functions/v1/send-contact-email";

export default function CTASection() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setFormError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot check
    const honeypotValue = (formData.get("website_alt") as string || "").trim();
    if (honeypotValue) {
      // Bot detected - show fake success
      setFormStatus("success");
      form.reset();
      setTimeout(() => {
        setIsFormOpen(false);
        setFormStatus("idle");
      }, 2000);
      return;
    }

    const email = formData.get("email") as string;

    // Build body from form fields (exclude email and honeypot)
    const body: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== "email" && key !== "website_alt") {
        body[key] = value.toString();
      }
    });

    try {
      const response = await fetch(EDGE_FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: PROJECT_ID,
          email,
          body,
        }),
      });

      const responseText = await response.text();
      let parsed: { success?: boolean; message?: string; error?: string; messageId?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // Non-JSON response
      }

      if (response.ok && parsed?.success === true) {
        setFormStatus("success");
        form.reset();
        setTimeout(() => {
          setIsFormOpen(false);
          setFormStatus("idle");
        }, 2000);
      } else {
        const serverMsg =
          parsed?.error ||
          parsed?.message ||
          "送信に失敗しました。お手数ですが、再度お試しください。";
        setFormError(serverMsg);
        setFormStatus("error");
      }
    } catch {
      setFormError("ネットワークエラーが発生しました。お手数ですが、再度お試しください。");
      setFormStatus("error");
    }
  };

  return (
    <>
      <section className="w-full bg-white scroll-mt-20" id="contact">
        <div className="flex flex-col lg:flex-row min-h-[500px] md:min-h-[600px]">
          {/* Left Column - Contact Info */}
          <div className="w-full lg:w-1/2 bg-[#E60073] flex flex-col justify-center py-12 md:py-16 lg:py-20 px-6 md:px-10 lg:px-16">
            <div className="mb-8">
              <span className="text-white/70 text-xs block mb-2">&bull; お問い合わせ</span>
              <h2 className="text-white text-2xl md:text-3xl font-light mb-4">
                お問い合わせ・ご相談
              </h2>
              <p className="text-white/80 text-sm leading-relaxed">
                TBM健康美容研究所は、美容業界の皆様の夢や目標を叶えるために、
                教材・出版、セミナー、コンサルティング、検定など、多様なサービスを提供しています。
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white flex-shrink-0">
                  <i className="ri-phone-line text-sm"></i>
                </span>
                <div>
                  <p className="text-white/60 text-xs mb-1">TEL</p>
                  <p className="text-white font-medium text-lg">027-310-2220</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white flex-shrink-0">
                  <i className="ri-printer-line text-sm"></i>
                </span>
                <div>
                  <p className="text-white/60 text-xs mb-1">FAX</p>
                  <p className="text-white font-medium text-lg">027-327-6344</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 text-white flex-shrink-0">
                  <i className="ri-map-pin-line text-sm"></i>
                </span>
                <div>
                  <p className="text-white/60 text-xs mb-1">所在地</p>
                  <p className="text-white text-sm">〒370-0841 群馬県高崎市栄町13-1</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 bg-white text-gamo-green text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 hover:bg-white/90 whitespace-nowrap w-fit cursor-pointer"
            >
              <i className="ri-mail-line text-sm"></i>
              <span>お問い合わせフォーム</span>
            </button>
          </div>

          {/* Right Column - Photo */}
          <div className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-full">
            <img
              src="https://media.genaiweb.ai/73acebca-6391-4da3-b270-472de16380f3/1f596b08.jpeg"
              alt="Contact TBM"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => { if (formStatus !== "submitting") setIsFormOpen(false); }} />
          <div className="relative bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in-up">
            <button
              onClick={() => { if (formStatus !== "submitting") setIsFormOpen(false); }}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line"></i>
            </button>

            <div className="p-6 md:p-8">
              <h3 className="text-xl font-bold text-gamo-dark-gray mb-1">お問い合わせ</h3>
              <p className="text-sm text-gray-500 mb-6">下記フォームにご記入の上、送信してください。</p>

              {formStatus === "success" ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-gamo-green/10 flex items-center justify-center mb-4">
                    <i className="ri-check-line text-2xl text-gamo-green"></i>
                  </div>
                  <p className="text-gamo-dark-gray font-medium text-lg">送信が完了しました</p>
                  <p className="text-gray-500 text-sm mt-1">お問い合わせいただきありがとうございます。</p>
                </div>
              ) : formStatus === "error" ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                  </div>
                  <p className="text-gamo-dark-gray font-medium text-lg">送信に失敗しました</p>
                  <p className="text-gray-500 text-sm mt-1 text-center max-w-xs">{formError}</p>
                  <button
                    onClick={() => setFormStatus("idle")}
                    className="mt-4 inline-flex items-center gap-2 bg-gamo-green text-white text-sm font-medium px-6 py-2.5 rounded-full hover:bg-gamo-green-dark transition-colors cursor-pointer"
                  >
                    もう一度試す
                  </button>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form="true" onSubmit={handleSubmit} className="space-y-4">
                  {/* Anti-spam honeypot */}
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
                  />

                  {/* Name */}
                  <div>
                    <label htmlFor="form-name" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      お名前 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="form-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50"
                      placeholder="例：山田 太郎"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="form-email" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      メールアドレス <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="form-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50"
                      placeholder="例：taro@example.com"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label htmlFor="form-phone" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      電話番号
                    </label>
                    <input
                      id="form-phone"
                      name="phone"
                      type="tel"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50"
                      placeholder="例：027-310-2220"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label htmlFor="form-company" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      会社名 / 団体名
                    </label>
                    <input
                      id="form-company"
                      name="company"
                      type="text"
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50"
                      placeholder="例：株式会社〇〇"
                    />
                  </div>

                  {/* Inquiry Type */}
                  <div>
                    <label htmlFor="form-type" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      お問い合わせ種別 <span className="text-red-400">*</span>
                    </label>
                    <select
                      id="form-type"
                      name="inquiry_type"
                      required
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50 appearance-none cursor-pointer"
                    >
                      <option value="">選択してください</option>
                      <option value="教材・出版">教材・出版について</option>
                      <option value="セミナー・スクール">セミナー・スクールについて</option>
                      <option value="コンサルティング">コンサルティングについて</option>
                      <option value="サロン">サロン経営について</option>
                      <option value="検定">総合美容検定について</option>
                      <option value="健康事業">健康事業について</option>
                      <option value="新製品化">新製品化について</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="form-message" className="block text-sm font-medium text-gamo-dark-gray mb-1.5">
                      お問い合わせ内容 <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      id="form-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gamo-green/30 focus:border-gamo-green transition-colors bg-gray-50 resize-none"
                      placeholder="お問い合わせ内容を500文字以内でご記入ください。"
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">最大500文字</p>
                  </div>

                  {/* Submit */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full flex items-center justify-center gap-2 bg-gamo-green text-white text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 hover:bg-gamo-green-dark disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {formStatus === "submitting" ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          <span>送信中...</span>
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line text-sm"></i>
                          <span>送信する</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}